// src/portal-app/tabs/TeamTab.js
//
// P2 — lean hero + condensed cards. This tab's single reason to exist is
// answering "who is working right now" (LeanHero + avatars) — that is
// deliberately the only tall thing on the screen. Everything else (Needs
// Attention, Manage) is compact list rows, never another hero-sized card.
import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useNav } from '../nav/NavStack';
import { useHubCards } from '../nav/useHubCards';
import {
    LeanHero, AttentionList, CondensedRow, ensureKitCss,
} from '../shell/kit';
import { fmtMoney } from '../insights/kit';
import { formatDateLabel } from '../nav/dateRange';
import { useTeamData } from './data/useTeamData';
import { ensureTabCss } from './tabCss';
import { HubEmptyState } from './HubEmptyState';

/* ---- local icon set — the kit's shared icons.js has no people/calendar/
   card glyphs, so these are defined here, matching MoreTab.js's precedent
   of keeping a card-specific icon local rather than growing the shared set
   for a single consumer. Inline SVG only, never emoji (spec §4). ---- */
const IconUsers = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="9" cy="8" r="3.2" />
        <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
        <path d="M15.5 5.4a3.2 3.2 0 0 1 0 5.2M17.6 14a5.5 5.5 0 0 1 3 5" />
    </svg>
);

const IconCalendar = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
        <path d="M3.5 9.8h17" />
        <path d="M8 2.8v3.6M16 2.8v3.6" />
    </svg>
);

const IconPayRuns = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2.8" y="6" width="18.4" height="12" rx="2.5" />
        <circle cx="12" cy="12" r="2.6" />
    </svg>
);

const LockIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    </svg>
);

const CARD_ICON = { employees: IconUsers, schedule: IconCalendar, payruns: IconPayRuns };
const CARD_TONE = { employees: 'blue', schedule: 'teal', payruns: 'purple' };

// AttentionList's tone (its dot) and CondensedRow's pill both read urgency
// the same way: red -> red, amber -> amber, green -> muted. A "green" (safe)
// pay run isn't worth an eye-catching green pill on a list of exceptions, so
// it de-escalates to muted rather than celebrating — matching the mapping
// this exact rule already uses for the AttentionList row (spec brief item 25).
const URGENCY_TONE = { red: 'red', amber: 'amber', green: 'muted' };

// Days between today and a backend 'YYYY-MM-DD' payDate, parsed at local
// noon (matches nav/dateRange.js's parseLocalDay convention) so the diff is
// exact across DST and never off-by-one via toISOString().
function daysUntilPayDate(payDate, now = new Date()) {
    if (!payDate) return null;
    const target = new Date(`${payDate}T12:00:00`);
    if (Number.isNaN(target.getTime())) return null;
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
    return Math.round((target.getTime() - today.getTime()) / 86400000);
}

// Task 8: relative phrasing ("Due in 3 days") replaces Task 4's invented
// Overdue/Due soon/On track enum, which had no in-repo precedent — the
// approved mockup (04-team-hero-cards.html) pills the exact same row with
// "Due in 3 days", and a day count reads more useful to a manager scanning
// the tab than a vague bucket. Tone still comes from the backend's
// red/amber/green urgency (URGENCY_TONE), unaffected by this change.
function dueLabel(t, payDate) {
    const days = daysUntilPayDate(payDate);
    if (days == null) return '';
    if (days < 0) return t('due_overdue_by_days', { n: Math.abs(days) }, 'Overdue by {n} days');
    if (days === 0) return t('due_today', {}, 'Due today');
    if (days === 1) return t('due_in_one_day', {}, 'Due in 1 day');
    return t('due_in_days', { n: days }, 'Due in {n} days');
}

// Whole-hours-or-one-decimal, e.g. 62 or 62.5 — never 62.333333333333336.
function fmtHours(n) {
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function lockedRow(card, t) {
    return {
        icon: <LockIcon />,
        tone: CARD_TONE[card.id],
        name: t(card.labelKey, {}, card.fallback),
        value: <span className="tab-locked-hint">{t('hub_locked_hint', {}, 'Upgrade to unlock')}</span>,
        onPress: undefined,
    };
}

function buildEmployeesRow(card, data, t, push) {
    if (card.state === 'locked') return lockedRow(card, t);
    return {
        icon: <IconUsers />,
        tone: CARD_TONE.employees,
        name: t(card.labelKey, {}, card.fallback),
        value: data.activeEmployees == null ? '—' : data.activeEmployees,
        secondary: t('n_on_shift', { n: data.onShiftNow }, '{n} on shift'),
        onPress: card.state === 'active' ? () => push('employees') : undefined,
    };
}

function buildScheduleRow(card, data, t, push) {
    if (card.state === 'locked') return lockedRow(card, t);
    const shiftsPart = t('n_shifts_today', { n: data.scheduledToday }, '{n} shifts today');
    const hoursPart = data.hoursThisWeek == null
        ? null
        : t('n_hours_this_week', { n: fmtHours(data.hoursThisWeek) }, '{n} hrs this week');
    const conflicts = Array.isArray(data.conflicts) ? data.conflicts : [];
    return {
        icon: <IconCalendar />,
        tone: CARD_TONE.schedule,
        name: t(card.labelKey, {}, card.fallback),
        value: data.scheduledToday,
        secondary: [shiftsPart, hoursPart].filter(Boolean).join(' · '),
        pill: conflicts.length > 0
            ? { label: t('n_conflicts', { n: conflicts.length }, '{n} conflicts'), tone: 'amber' }
            : undefined,
        onPress: card.state === 'active' ? () => push('schedule') : undefined,
    };
}

function buildPayrunsRow(card, data, t, push, currency) {
    if (card.state === 'locked') return lockedRow(card, t);
    const due = Array.isArray(data.payRunsDue) ? data.payRunsDue : [];
    const totalOwed = due.reduce((sum, r) => sum + (Number(r.totalOwed) || 0), 0);
    const first = due[0];
    return {
        icon: <IconPayRuns />,
        tone: CARD_TONE.payruns,
        name: t(card.labelKey, {}, card.fallback),
        value: due.length === 0 ? '—' : fmtMoney(totalOwed, currency),
        secondary: first ? t('due_date', { date: formatDateLabel(first.payDate) }, 'Due {date}') : undefined,
        pill: first
            ? { label: dueLabel(t, first.payDate), tone: URGENCY_TONE[first.urgency] || 'muted' }
            : undefined,
        onPress: card.state === 'active' ? () => push('payruns') : undefined,
    };
}

const ROW_BUILDERS = {
    employees: buildEmployeesRow,
    schedule: buildScheduleRow,
    payruns: buildPayrunsRow,
};

export default function TeamTab() {
    ensureKitCss();
    ensureTabCss();
    const { t } = useLanguage();
    const { getCurrency } = useAuth() || {};
    const { push } = useNav();
    const { status, data, reload } = useTeamData();
    const cards = useHubCards('team');

    // Local calendar day, never UTC — a portal on the west side of midnight
    // UTC must not show tomorrow's date (see nav/dateRange.js header note).
    const dateLine = new Date().toLocaleDateString(undefined, {
        weekday: 'long', day: 'numeric', month: 'long',
    });

    const header = (
        <>
            <h1 className="tab-title">{t('tab_team', {}, 'Team')}</h1>
            <p className="tab-sub">{dateLine}</p>
        </>
    );

    if (status === 'loading') {
        return (
            <div className="tab-wrap">
                {header}
                <div data-testid="team-skeleton" aria-hidden="true">
                    <div className="tab-skel" style={{ height: 120 }} />
                    <div className="tab-skel" style={{ height: 60 }} />
                    <div className="tab-skel" style={{ height: 240 }} />
                </div>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="tab-wrap">
                {header}
                <div className="tab-state" data-testid="team-error">
                    <p className="tab-empty-title">{t('team_error_title', {}, "Couldn't load your team")}</p>
                    <p className="tab-empty-msg">
                        {t('team_error_msg', {}, 'Check your connection and try again.')}
                    </p>
                    <button type="button" className="tab-retry" onClick={reload}>
                        {t('retry', {}, 'Retry')}
                    </button>
                </div>
            </div>
        );
    }

    const currency = getCurrency ? getCurrency() : '£';

    // Absent is not zero: activeEmployees is null when its secondary call
    // failed, and the suffix must be omitted entirely rather than read
    // "of null staff" or silently substitute 0 (spec, correctness point 1).
    const heroSuffix = data.activeEmployees == null
        ? undefined
        : t('of_n_staff', { n: data.activeEmployees }, 'of {n} staff');

    const heroPill = (data.onShiftNow === data.scheduledToday && data.scheduledToday > 0)
        ? { label: t('all_clocked_in', {}, 'All clocked in'), tone: 'green' }
        : undefined;

    const heroAvatars = (Array.isArray(data.onShift) ? data.onShift : [])
        .map((p) => ({ id: p.id, initials: p.initials }));

    const cardById = {};
    cards.forEach((c) => { cardById[c.id] = c; });
    const scheduleActive = cardById.schedule && cardById.schedule.state === 'active';
    const payrunsActive = cardById.payruns && cardById.payruns.state === 'active';

    const conflictItems = (Array.isArray(data.conflicts) ? data.conflicts : []).map((c, i) => ({
        id: `conflict-${i}`,
        tone: 'amber',
        text: t(
            'schedule_conflict_on',
            { name: c.employeeName, date: formatDateLabel(c.date) },
            '{name} has overlapping shifts on {date}',
        ),
        chip: { label: t('fix', {}, 'Fix'), tone: 'amber' },
        onPress: scheduleActive ? () => push('schedule') : undefined,
    }));

    const payRunItems = (Array.isArray(data.payRunsDue) ? data.payRunsDue : []).map((r) => ({
        id: `payrun-${r.id}`,
        tone: URGENCY_TONE[r.urgency] || 'muted',
        text: t('pay_run_due', { date: formatDateLabel(r.payDate) }, 'Pay run due {date}'),
        chip: { label: fmtMoney(r.totalOwed, currency), tone: 'muted' },
        onPress: payrunsActive ? () => push('payruns') : undefined,
    }));

    return (
        <div className="tab-wrap">
            {header}

            <div className="tab-group">
                <LeanHero
                    label={t('on_shift_now', {}, 'On shift now')}
                    value={data.onShiftNow}
                    suffix={heroSuffix}
                    pill={heroPill}
                    avatars={heroAvatars}
                />
            </div>

            {/* AttentionList renders nothing of its own when empty — no
                heading is added here, or it would outlive the list it labels
                (spec, correctness point 3). */}
            <AttentionList items={[...conflictItems, ...payRunItems]} />

            {cards.length === 0 ? (
                <HubEmptyState t={t} testId="team-empty" />
            ) : (
                <>
                    <div className="pk-section-label">{t('manage', {}, 'Manage')}</div>
                    <div className="tab-group" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {cards.map((card) => {
                            const build = ROW_BUILDERS[card.id];
                            if (!build) return null;
                            const row = build(card, data, t, push, currency);
                            return <CondensedRow key={card.id} testId={card.id} {...row} />;
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
